// DQ-8736 — route -> page title mapping for the dynamic browser tab title.
//
// The tab title is rendered as `DQ | <Page Name>`; this table supplies the
// page name for the current route. Entries are matched in order with
// react-router's `matchPath`, so more specific paths (e.g. "/domain/add")
// are listed before the generic ones they would otherwise be shadowed by
// (e.g. "/domain"). Any route without an entry falls back to
// `pageTitles.fallback`, which keeps the title from ever going blank.

import { matchPath } from "react-router-dom";

export const TITLE_PREFIX = "DQ";

export const FALLBACK_TITLE_KEY = "pageTitles.fallback";

export interface PageTitleRoute {
  /** Route pattern, in the same syntax used by the <Route path> definitions. */
  readonly path: string;
  /** i18n key under the `pageTitles` namespace resolving to the page name. */
  readonly titleKey: string;
}

export const PAGE_TITLE_ROUTES: readonly PageTitleRoute[] = [
  { path: "/login", titleKey: "pageTitles.signIn" },
  { path: "/signup", titleKey: "pageTitles.signUp" },
  { path: "/forgotpassword", titleKey: "pageTitles.forgotPassword" },
  { path: "/403", titleKey: "pageTitles.forbidden" },
  { path: "/domain/add", titleKey: "pageTitles.domainAdd" },
  { path: "/domain/import-entity", titleKey: "pageTitles.domainImport" },
  { path: "/domain/view/:id", titleKey: "pageTitles.domainView" },
  { path: "/domain/edit/:id", titleKey: "pageTitles.domainEdit" },
  { path: "/domain/auditDomain/:id", titleKey: "pageTitles.domainAudit" },
  { path: "/domain", titleKey: "pageTitles.domain" },
  { path: "/domain-linkage/add", titleKey: "pageTitles.domainLinkageAdd" },
  {
    path: "/domain-linkage/edit/:id",
    titleKey: "pageTitles.domainLinkageEdit",
  },
  {
    path: "/domain-linkage/view/:id",
    titleKey: "pageTitles.domainLinkageView",
  },
  { path: "/domain-linkage", titleKey: "pageTitles.domainLinkage" },
  { path: "/resource/add/:domainId?", titleKey: "pageTitles.resourceAdd" },
  { path: "/resource/import-entity", titleKey: "pageTitles.resourceImport" },
  { path: "/resource/:domainId/view/:id", titleKey: "pageTitles.resourceView" },
  { path: "/resource/:domainId/edit/:id", titleKey: "pageTitles.resourceEdit" },
  {
    path: "/resource/:domainId/auditResource/:id",
    titleKey: "pageTitles.resourceAudit",
  },
  {
    path: "/resource/:domainId/researchQuery/:id",
    titleKey: "pageTitles.resourceResearchQuery",
  },
  {
    path: "/resource/:domainId/validate-result",
    titleKey: "pageTitles.resourceValidationResult",
  },
  {
    path: "/resource/:domainId/validate-resource/:id",
    titleKey: "pageTitles.resourceValidate",
  },
  {
    path: "/resource/:domainId/re-run-validate-resource/:id",
    titleKey: "pageTitles.resourceValidateReRun",
  },
  { path: "/resource/:domainId?", titleKey: "pageTitles.resource" },
  {
    path: "/resource-columns/add-columns",
    titleKey: "pageTitles.resourceColumnsAdd",
  },
  {
    path: "/resource-column/import-entity",
    titleKey: "pageTitles.resourceColumnImport",
  },
  {
    path: "/resource-columns/:domainId/view/:id",
    titleKey: "pageTitles.resourceColumnView",
  },
  {
    path: "/resource-columns/:domainId/edit/:id",
    titleKey: "pageTitles.resourceColumnEdit",
  },
  {
    path: "/resource-columns/:domainId/auditResourceColumn/:id",
    titleKey: "pageTitles.resourceColumnAudit",
  },
  {
    path: "/resource-columns/:domainId",
    titleKey: "pageTitles.resourceColumns",
  },
  { path: "/rules/add", titleKey: "pageTitles.ruleAdd" },
  { path: "/rules/import-entity", titleKey: "pageTitles.rulesImport" },
  { path: "/rules/:domainId/edit/:id", titleKey: "pageTitles.ruleEdit" },
  { path: "/rules/:domainId/view/:id", titleKey: "pageTitles.ruleView" },
  {
    path: "/rules/:domainId/researchQuery/:id",
    titleKey: "pageTitles.ruleResearchQuery",
  },
  {
    path: "/rules/:domainId/rule-execution/:ruleId",
    titleKey: "pageTitles.ruleExecution",
  },
  {
    path: "/rules/:domainId/re-run-rule-execution/:ruleId",
    titleKey: "pageTitles.ruleExecutionReRun",
  },
  { path: "/rules/:domainId/auditRule/:id", titleKey: "pageTitles.ruleAudit" },
  { path: "/rules-list/:domainId?", titleKey: "pageTitles.rulesList" },
  { path: "/rule-history", titleKey: "pageTitles.ruleHistory" },
  {
    path: "/rules-execution-history/consolidate-results",
    titleKey: "pageTitles.comparisonSummary",
  },
  {
    path: "/rules-execution-history/:id/:ruleResultId/dashboard",
    titleKey: "pageTitles.comparisonResultDashboard",
  },
  {
    path: "/rules-execution-history",
    titleKey: "pageTitles.comparisonHistory",
  },
  { path: "/research-query/add", titleKey: "pageTitles.researchQueryAdd" },
  {
    path: "/research-query/import-entity",
    titleKey: "pageTitles.researchQueryImport",
  },
  {
    path: "/research-query/view/:id",
    titleKey: "pageTitles.researchQueryView",
  },
  {
    path: "/research-query/edit/:id",
    titleKey: "pageTitles.researchQueryEdit",
  },
  {
    path: "/research-query/auditGenericResearchQuery/:id",
    titleKey: "pageTitles.researchQueryAudit",
  },
  {
    path: "/research-query/research-query-execution/:id",
    titleKey: "pageTitles.researchQueryExecution",
  },
  { path: "/research-query", titleKey: "pageTitles.researchQuery" },
  {
    path: "/research-execution-history/re-run-research-query-execution/:id",
    titleKey: "pageTitles.researchQueryExecutionReRun",
  },
  {
    path: "/research-execution-history/:researchQueryId/:execution_id/dashboard",
    titleKey: "pageTitles.researchQueryResultDashboard",
  },
  {
    path: "/research-execution-history",
    titleKey: "pageTitles.researchQueryHistory",
  },
  {
    path: "/validation-execution-history/validate-result/:resourceResultId",
    titleKey: "pageTitles.validationResult",
  },
  {
    path: "/validation-execution-history/validation-execution-results",
    titleKey: "pageTitles.validationSummary",
  },
  {
    path: "/validation-execution-history",
    titleKey: "pageTitles.validationHistory",
  },
  {
    path: "/validations-issue-list",
    titleKey: "pageTitles.validationIssueList",
  },
  { path: "/issue-list", titleKey: "pageTitles.issueList" },
  {
    path: "/incident-reporting-rerun-report",
    titleKey: "pageTitles.incidentReportingReRunReport",
  },
  {
    path: "/incident-reporting-rerun",
    titleKey: "pageTitles.incidentReportingReRun",
  },
  { path: "/files-list", titleKey: "pageTitles.filesList" },
  { path: "/blob-list", titleKey: "pageTitles.blobList" },
  { path: "/sftp-list", titleKey: "pageTitles.sftpList" },
  { path: "/dashboard-v2", titleKey: "pageTitles.dashboardV2" },
  { path: "/dashboard", titleKey: "pageTitles.dashboard" },
  { path: "/linked-services/add", titleKey: "pageTitles.linkedServiceAdd" },
  {
    path: "/linked-services/import-entity",
    titleKey: "pageTitles.linkedServicesImport",
  },
  {
    path: "/linked-services/view/:id",
    titleKey: "pageTitles.linkedServiceView",
  },
  {
    path: "/linked-services/edit/:id",
    titleKey: "pageTitles.linkedServiceEdit",
  },
  {
    path: "/linked-services/auditLinkedServices/:id",
    titleKey: "pageTitles.linkedServiceAudit",
  },
  { path: "/linked-services", titleKey: "pageTitles.linkedServices" },
  { path: "/connection-keys/add", titleKey: "pageTitles.connectionKeyAdd" },
  {
    path: "/connection-keys/import-entity",
    titleKey: "pageTitles.connectionKeysImport",
  },
  {
    path: "/connection-keys/edit/:id",
    titleKey: "pageTitles.connectionKeyEdit",
  },
  {
    path: "/connection-keys/view/:id",
    titleKey: "pageTitles.connectionKeyView",
  },
  {
    path: "/connection-keys/auditConnectionKey/:id",
    titleKey: "pageTitles.connectionKeyAudit",
  },
  { path: "/connection-keys", titleKey: "pageTitles.connectionKeys" },
  {
    path: "/connection-runtime-config/add",
    titleKey: "pageTitles.connectionRuntimeConfigAdd",
  },
  {
    path: "/connection-runtime-config/view/:id",
    titleKey: "pageTitles.connectionRuntimeConfigView",
  },
  {
    path: "/connection-runtime-config/edit/:id",
    titleKey: "pageTitles.connectionRuntimeConfigEdit",
  },
  {
    path: "/connection-runtime-config",
    titleKey: "pageTitles.connectionRuntimeConfig",
  },
  { path: "/run-instance/add", titleKey: "pageTitles.runInstanceAdd" },
  {
    path: "/run-instance/import-entity",
    titleKey: "pageTitles.runInstanceImport",
  },
  { path: "/run-instance/view/:id", titleKey: "pageTitles.runInstanceView" },
  { path: "/run-instance/edit/:id", titleKey: "pageTitles.runInstanceEdit" },
  { path: "/run-instance", titleKey: "pageTitles.runInstance" },
  {
    path: "/execution-sub-group/add",
    titleKey: "pageTitles.executionSubGroupAdd",
  },
  {
    path: "/execution-sub-group/view/:id",
    titleKey: "pageTitles.executionSubGroupView",
  },
  {
    path: "/execution-sub-group/edit/:id",
    titleKey: "pageTitles.executionSubGroupEdit",
  },
  { path: "/execution-sub-group", titleKey: "pageTitles.executionSubGroup" },
  {
    path: "/comparison-research-queries/manage",
    titleKey: "pageTitles.comparisonResearchQueryManage",
  },
  {
    path: "/comparison-research-queries/import-entity",
    titleKey: "pageTitles.comparisonResearchQueryImport",
  },
  {
    path: "/comparison-research-queries/view/:id",
    titleKey: "pageTitles.comparisonResearchQueryView",
  },
  {
    path: "/comparison-research-queries/edit/:id",
    titleKey: "pageTitles.comparisonResearchQueryEdit",
  },
  {
    path: "/comparison-research-queries/auditComparison/:id",
    titleKey: "pageTitles.comparisonResearchQueryAudit",
  },
  {
    path: "/comparison-research-queries",
    titleKey: "pageTitles.comparisonResearchQueries",
  },
  { path: "/system/add", titleKey: "pageTitles.systemAdd" },
  { path: "/system/view/:id", titleKey: "pageTitles.systemView" },
  { path: "/system/edit/:id", titleKey: "pageTitles.systemEdit" },
  { path: "/system", titleKey: "pageTitles.system" },
  { path: "/classification/add", titleKey: "pageTitles.classificationAdd" },
  {
    path: "/classification/view/:id",
    titleKey: "pageTitles.classificationView",
  },
  {
    path: "/classification/edit/:id",
    titleKey: "pageTitles.classificationEdit",
  },
  { path: "/classification", titleKey: "pageTitles.classification" },
  { path: "/reason/add", titleKey: "pageTitles.reasonAdd" },
  { path: "/reason/view/:id", titleKey: "pageTitles.reasonView" },
  { path: "/reason/edit/:id", titleKey: "pageTitles.reasonEdit" },
  { path: "/reason", titleKey: "pageTitles.reason" },
  {
    path: "/file-processing-attributes/add",
    titleKey: "pageTitles.fileProcessingAttributeAdd",
  },
  {
    path: "/file-processing-attributes/import-entity",
    titleKey: "pageTitles.fileProcessingAttributesImport",
  },
  {
    path: "/file-processing-attributes/view/:id",
    titleKey: "pageTitles.fileProcessingAttributeView",
  },
  {
    path: "/file-processing-attributes/edit/:id",
    titleKey: "pageTitles.fileProcessingAttributeEdit",
  },
  {
    path: "/file-processing-attributes",
    titleKey: "pageTitles.fileProcessingAttributes",
  },
  { path: "/admin", titleKey: "pageTitles.admin" },
  { path: "/users/edit/:id", titleKey: "pageTitles.userEdit" },
  { path: "/users/view/:id", titleKey: "pageTitles.userView" },
  { path: "/users", titleKey: "pageTitles.users" },
  { path: "/roles/add", titleKey: "pageTitles.roleAdd" },
  { path: "/roles/edit/:id", titleKey: "pageTitles.roleEdit" },
  { path: "/roles/view/:id", titleKey: "pageTitles.roleView" },
  { path: "/roles", titleKey: "pageTitles.roles" },
  { path: "/groups/add", titleKey: "pageTitles.groupAdd" },
  { path: "/groups/edit/:id", titleKey: "pageTitles.groupEdit" },
  { path: "/groups/view/:id", titleKey: "pageTitles.groupView" },
  { path: "/groups/:id/assign-users", titleKey: "pageTitles.groupAssignUsers" },
  { path: "/groups", titleKey: "pageTitles.groups" },
  { path: "/tags/add", titleKey: "pageTitles.tagAdd" },
  { path: "/tags/edit/:id", titleKey: "pageTitles.tagEdit" },
  { path: "/tags/view/:id", titleKey: "pageTitles.tagView" },
  { path: "/tags", titleKey: "pageTitles.tags" },
  { path: "/scoring-config", titleKey: "pageTitles.scoringConfig" },
  { path: "/scoring-defaults", titleKey: "pageTitles.scoringDefaults" },
  {
    path: "/scoring/validation/add",
    titleKey: "pageTitles.validationScoringAdd",
  },
  {
    path: "/scoring/validation/edit/:id",
    titleKey: "pageTitles.validationScoringEdit",
  },
  {
    path: "/scoring/validation/view/:id",
    titleKey: "pageTitles.validationScoringView",
  },
  {
    path: "/scoring/validation/audit/:id",
    titleKey: "pageTitles.validationScoringAudit",
  },
  { path: "/scoring/validation", titleKey: "pageTitles.validationScoring" },
  {
    path: "/scoring/comparison/add",
    titleKey: "pageTitles.comparisonScoringAdd",
  },
  {
    path: "/scoring/comparison/edit/:id",
    titleKey: "pageTitles.comparisonScoringEdit",
  },
  {
    path: "/scoring/comparison/view/:id",
    titleKey: "pageTitles.comparisonScoringView",
  },
  {
    path: "/scoring/comparison/audit/:id",
    titleKey: "pageTitles.comparisonScoringAudit",
  },
  { path: "/scoring/comparison", titleKey: "pageTitles.comparisonScoring" },
  {
    path: "/scoring/generic-rq/add",
    titleKey: "pageTitles.genericRQScoringAdd",
  },
  {
    path: "/scoring/generic-rq/edit/:id",
    titleKey: "pageTitles.genericRQScoringEdit",
  },
  {
    path: "/scoring/generic-rq/view/:id",
    titleKey: "pageTitles.genericRQScoringView",
  },
  {
    path: "/scoring/generic-rq/audit/:id",
    titleKey: "pageTitles.genericRQScoringAudit",
  },
  { path: "/scoring/generic-rq", titleKey: "pageTitles.genericRQScoring" },
  {
    path: "/scoring/comparison-run/add",
    titleKey: "pageTitles.comparisonRunScoringAdd",
  },
  {
    path: "/scoring/comparison-run/edit/:id",
    titleKey: "pageTitles.comparisonRunScoringEdit",
  },
  {
    path: "/scoring/comparison-run/view/:id",
    titleKey: "pageTitles.comparisonRunScoringView",
  },
  {
    path: "/scoring/comparison-run",
    titleKey: "pageTitles.comparisonRunScoring",
  },
  {
    path: "/scoring/validation-run/add",
    titleKey: "pageTitles.validationRunScoringAdd",
  },
  {
    path: "/scoring/validation-run/edit/:id",
    titleKey: "pageTitles.validationRunScoringEdit",
  },
  {
    path: "/scoring/validation-run/view/:id",
    titleKey: "pageTitles.validationRunScoringView",
  },
  {
    path: "/scoring/validation-run/audit/:runId/:resourceId",
    titleKey: "pageTitles.validationRunScoringAudit",
  },
  {
    path: "/scoring/validation-run",
    titleKey: "pageTitles.validationRunScoring",
  },
  {
    path: "/scoring/research-query-run/add",
    titleKey: "pageTitles.researchQueryRunScoringAdd",
  },
  {
    path: "/scoring/research-query-run/edit/:id",
    titleKey: "pageTitles.researchQueryRunScoringEdit",
  },
  {
    path: "/scoring/research-query-run/view/:id",
    titleKey: "pageTitles.researchQueryRunScoringView",
  },
  {
    path: "/scoring/research-query-run",
    titleKey: "pageTitles.researchQueryRunScoring",
  },
  {
    path: "/scoring/pipeline-weights/add",
    titleKey: "pageTitles.pipelineWeightsAdd",
  },
  {
    path: "/scoring/pipeline-weights/edit/:id",
    titleKey: "pageTitles.pipelineWeightsEdit",
  },
  {
    path: "/scoring/pipeline-weights/view/:id",
    titleKey: "pageTitles.pipelineWeightsView",
  },
  { path: "/scoring/pipeline-weights", titleKey: "pageTitles.pipelineWeights" },
  {
    path: "/scoring/run-overview/:runInstanceId",
    titleKey: "pageTitles.runScoreOverview",
  },
  {
    path: "/results/run-score-history",
    titleKey: "pageTitles.runScoreHistory",
  },
  {
    path: "/results/run-score-dashboard/:runId",
    titleKey: "pageTitles.runScoreDashboard",
  },
  { path: "/profile/change-password", titleKey: "pageTitles.changePassword" },
  { path: "/profile", titleKey: "pageTitles.profile" },
] as const;

/**
 * Resolves the i18n title key for a pathname, falling back to the generic key
 * when no route matches.
 */
export const getPageTitleKey = (pathname: string): string =>
  PAGE_TITLE_ROUTES.find((route) => matchPath(route.path, pathname))
    ?.titleKey ?? FALLBACK_TITLE_KEY;

/** Builds the full document title, e.g. "DQ | Add Resource". */
export const buildDocumentTitle = (pageName: string): string =>
  `${TITLE_PREFIX} | ${pageName}`;
